import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Alert, Button, Card} from "react-bootstrap";
import MaterialTable, {Column, MTableBody} from "material-table";
import {getOptionsByField, localization, modifyDefaultOptions, tableIconsModified} from "../../helpers/table";
import {TableCell, TableFooter, TableHead, TableRow} from "@material-ui/core";
import Select from "react-select";
import useAPI from "../../hooks/useAPI";
import {useParams} from "react-router-dom";
import useToast from "../../hooks/useToast";
import AsyncSelect from "react-select/async";
import {useMediaQuery} from "@mui/material";
import {MonetaryFloatInput} from "../common/MonetaryFloatInput";
import './BuyDivisions.less'
import uuid from "react-uuid";
import {range} from "../../helpers";

type BuyStructuralAffiliationFilters = {
    divisionName: string[],
    instituteName: string[]
}

type Option<T> = {
    label: string,
    value: T
}

type BuyStructuralAffiliationData = {
    id: number | undefined,
    buyId: number
    divisionName?: string,
    divisionUuid?: string,
    instituteName: string,
    instituteUuid: string,
    sums: { year: number, sum: number }[],
    arrayId?: string
}

type BuyDivisionsProps = {
    buy: {
        type: 'fb' | 'sf',
        buyKvrs: any[]
    },
    onChange: (
        value: {
            data: BuyStructuralAffiliationData[],
            metadata: { [key: string]: { year: number, total: number, totalKvrs: number } }
        }
    ) => void,
    invalidateTrigger: boolean
}

type Division = {
    guid: string,
    title: string,
    parentGuid: string,
    treePath: string
}

const divisionBranchGuids = ["561D2CEF-41FA-45FC-B3CD-3972643407D6", "9C5B558B-13E3-4C8F-870D-07A511E07766", "9D5A243E-3E58-4021-8EF7-988F969979E1", "A436E444-93BC-4EB4-B7F9-DF43EEDFC87D", "54EC42ED-BCB4-4982-B391-849D71709BC3"]

export default function BuyDivisions({buy, onChange, invalidateTrigger}: BuyDivisionsProps) {
    const {buyId} = useParams<{ buyId: string }>();
    const api = useAPI();
    const addToast = useToast();
    const isLargeScreen = useMediaQuery('(min-width: 1800px)');
    const isVeryLargeScreen = useMediaQuery('(min-width: 2100px)');

    const [data, setData] = useState<BuyStructuralAffiliationData[]>([]);
    const [divisionYears, setDivisionYears] = useState([]);
    const [availableYears, setAvailableYears] = useState<number[]>([]);
    const [institutes, setInstitutes] = useState<Division[]>([]);
    const [filters, setFilters] = useState<BuyStructuralAffiliationFilters>({
        divisionName: [],
        instituteName: []
    });

    const buyYear = useMemo(() => buy.year, [buy]);
    const numberFormatter = useMemo(() => new Intl.NumberFormat('ru', {minimumFractionDigits: 2}), []);
    const metadata = useMemo(() => {
        const startMetadata = Object.fromEntries(availableYears.map(year => ([year, {year, total: 0, totalKvrs: 0}])))
        data.forEach(curr => {
            curr.sums.forEach((valueByYear) => {
                if(startMetadata[valueByYear.year]){
                    startMetadata[valueByYear.year].total += valueByYear.sum
                }
            })
        })
        buy.buyKvrs
            .filter(buyKvr => buy.type === 'fb' || buy.type === 'sf' && !['111.0', '119.0', '111.7', '119.7'].includes(buyKvr.costKvr?.num))
            .forEach(buyKvr => {
                buyKvr.sums.forEach((valueByYear: { year: number; draftSum: number; }) => {
                    if (startMetadata[valueByYear.year]) {
                        startMetadata[valueByYear.year].totalKvrs += valueByYear.draftSum
                    }
                })
            })
        return startMetadata
    }, [data, availableYears, buy]);
    const options = useMemo<{ [key: string]: Option<string>[] }>(() => Object.fromEntries(
        ['divisionName', 'instituteName'].map(fieldName => [fieldName, getOptionsByField(data, fieldName)])
    ), [data])
    const filteredData = useMemo(() => data.filter(value => Object
        .entries(filters)
        .map(([filterName, filterValue]) => filterValue.length === 0 || filterValue.includes(value[filterName as keyof BuyStructuralAffiliationFilters] || ''))
        .every(value => value)
    ), [data, filters])

    const handleAddRow = async (newValue: any) => {
        if (!newValue.division?.institute && !newValue.instituteName || newValue.division?.institute && !newValue.division.institute.title) {
            addToast.error('Поле "Институт/ректорат" должно быть заполнено!');
            return Promise.reject()
        }
        const buyDivision: BuyStructuralAffiliationData = {
            arrayId: uuid(),
            id: newValue.id,
            buyId: +buyId,
            instituteName: newValue.division?.institute?.title || newValue.instituteName,
            instituteUuid: newValue.division?.institute ? newValue.division.institute.guid : newValue.instituteUuid,
            divisionName: newValue.division?.division ? newValue.division.division.title : newValue.divisionName,
            divisionUuid: newValue.division?.division ? newValue.division.division.guid : newValue.divisionUuid,
            sums: availableYears.map(year => {
                if (newValue.hasOwnProperty(year)) return ({year, sum: newValue[year].sum || 0});
                const oldValue = newValue.sums?.find(value => value.year === year)
                return oldValue || {year, sum: 0}
            })
        }
        if (newValue.id || newValue.arrayId) {
            const dataCopy = [...data]
            const updatedValueIndex = dataCopy.findIndex(value => newValue.id ? value.id === newValue.id : value.arrayId === newValue.arrayId)
            dataCopy[updatedValueIndex] = buyDivision
            setData(dataCopy)
            return Promise.resolve()
        }
        setData(prevState => [...prevState, buyDivision])
        return Promise.resolve()
    }

    const handleDelete = async (value: BuyStructuralAffiliationData) => {
        setData(prevState => prevState.filter(division => division.id !== value.id))
    }

    const debounceRef = useRef<any>()
    const loadDivisionsOptions = (inputValue: string, callback: (options: Option<Division>[]) => void) => {
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(async () => {
            const allDivisions = await api.get(`/mng/divisions?title=${inputValue}`);
            callback(allDivisions.divs.map((division: Division) => ({label: division.title, value: division})))
        }, 350)
    }

    useEffect(() => {
        onChange({data, metadata})
    }, [data, metadata])

    useEffect(() => {
        const fetchDivisions = async () => {
            const divisions = buyId === 'new' ? {buyDivisions: [], availableYears: range(buyYear, new Date().getFullYear())} : await api.get(`/buys/${buyId}/divisions`);
            setDivisionYears(divisions.availableYears)
            setData(divisions.buyDivisions);
        }
        fetchDivisions()
    }, [buyId, invalidateTrigger, buyYear]);

    useEffect(() => {
        const getInstitutes = async () => {
            const institutesResponse = await api.get('/mng/institutes');
            setInstitutes(institutesResponse.institutes
                .sort((a, b) => a.title[0].toLowerCase() > b.title[0].toLowerCase() ? 1 : -1))
        }
        getInstitutes()
    }, [])

    useEffect(() => {
        setAvailableYears(buy.type === 'sf' ? divisionYears : [buyYear])
    }, [buyYear, divisionYears])

    return (
        <Card className='mb-4 buyDivisions'>
            <Card.Header className='p-4'>
                <Card.Title className='m-0'>
                    Принадлежность закупки к структурным подразделениям университета
                </Card.Title>
            </Card.Header>
            <Card.Body className='px-4'>
                <Alert>
                    Необходимо распределить оценочную стоимость
                    закупки {buy.type === 'sf' && '(за исключением расходов на оплату труда) '}по подразделениям и/или
                    институтам, в которых будет использован результат закупки
                </Alert>
                <MaterialTable<BuyStructuralAffiliationData>
                    data={filteredData}
                    localization={localization}
                    icons={{...tableIconsModified, Add: () => <Button variant='outline-primary'>+ Добавить</Button>}}
                    columns={[
                        {
                            title: 'Подразделение',
                            field: 'division',
                            editComponent: ({rowData, value, onChange}) => <AsyncSelect
                                loadOptions={loadDivisionsOptions}
                                menuPortalTarget={document.body}
                                placeholder={'Начните вводить название...'}
                                loadingMessage={() => 'Загрузка...'}
                                noOptionsMessage={() => ''}
                                isSearchable
                                isClearable
                                cacheOptions
                                onChange={(opt) => {
                                    let divisionInstitute: any = opt ?
                                        divisionBranchGuids.includes(opt.value.guid) ?
                                            {
                                                title: 'Филиалы и пр.',
                                                guid: null
                                            } :
                                            institutes.find(institute => opt.value.treePath.includes(institute.guid)) || {
                                                title: 'Ректорат',
                                                guid: null
                                            } : value?.institute
                                    onChange((opt ? {
                                        division: opt.value,
                                        institute: divisionInstitute
                                    } : {division: {title: null, guid: null}, institute: value?.institute}))
                                }}
                                value={value?.division ? {
                                    label: value.division.title,
                                    value: value.division.guid
                                } : {
                                    label: rowData.divisionName,
                                    value: rowData.divisionUuid
                                }}
                            />,
                            render: (rowData) => rowData.divisionName,
                            cellStyle: {
                                minWidth: isVeryLargeScreen ? 450 : isLargeScreen ? 375 : 250,
                                fontFamily: 'Roboto, sans-serif'
                            }
                        },
                        {
                            title: 'Институт/ректорат',
                            field: 'division',
                            editComponent: ({rowData, value, onChange}) => <Select
                                menuPortalTarget={document.body}
                                placeholder={'Начните вводить название...'}
                                isSearchable
                                isClearable
                                isDisabled={!!value?.divisionName}
                                options={institutes.map(institute => ({label: institute.title, value: institute}))}
                                onChange={(opt) => onChange(opt ?
                                    {institute: opt.value, division: {title: null, guid: null}}
                                    : {division: value?.division, institute: {title: undefined, guid: undefined}})}
                                value={value?.institute ? {
                                    label: value.institute.title,
                                    value: value.institute.guid
                                } : {
                                    label: rowData.instituteName,
                                    value: rowData.instituteUuid
                                }}
                            />,
                            render: rowData => rowData.instituteName,
                            cellStyle: {
                                minWidth: isVeryLargeScreen ? 450 : isLargeScreen ? 375 : 250,
                                fontFamily: 'Roboto, sans-serif'
                            }
                        },
                        ...Object.keys(metadata).map(year => ({
                            title: `${year}`,
                            field: `${year}`,
                            align: 'center',
                            headerStyle: {textAlign: 'center'},
                            cellStyle: {fontFamily: 'Roboto, sans-serif'},
                            render: rowData => {
                                const sum = rowData.sums.find(value => value.year === +year)?.sum;
                                return numberFormatter.format(sum ?? 0); // Если сумма не найдена, выводим 0
                            },
                            editComponent: ({rowData, value, onChange}) => <MonetaryFloatInput
                                value={(value || rowData)?.sums?.find(value => value.year === +year)?.sum}
                                onChange={(floatValue) => onChange({year: +year, sum: floatValue})}
                            />
                        }) as Column<BuyStructuralAffiliationData>)
                    ]}
                    options={modifyDefaultOptions({
                        filtering: false,
                        sorting: false
                    })}
                    components={{
                        Header: () => <TableHead>
                            <TableRow>
                                <TableCell>Действ.</TableCell>
                                <TableCell>Подразделение</TableCell>
                                <TableCell>Институт/ректорат</TableCell>
                                {Object.keys(metadata).length > 0 &&
                                  <TableCell align='center' colSpan={Object.keys(metadata).length}>Сумма
                                    (руб.)</TableCell>}
                            </TableRow>
                            <TableRow>
                                <TableCell/>
                                <TableCell>
                                    <Select<Option<string>, true>
                                        placeholder={null}
                                        noOptionsMessage={() => 'Ничего не найдено'}
                                        options={options.divisionName}
                                        onChange={(opts) => setFilters(prevState => ({
                                            ...prevState,
                                            divisionName: opts.map(opt => opt.value)
                                        }))}
                                        value={options.divisionName.filter((opt) => filters.divisionName.includes(opt.value))}
                                        isMulti
                                    />
                                </TableCell>
                                <TableCell>
                                    <Select<Option<string>, true>
                                        placeholder={null}
                                        noOptionsMessage={() => 'Ничего не найдено'}
                                        options={options.instituteName}
                                        onChange={(opts) => setFilters(prevState => ({
                                            ...prevState,
                                            instituteName: opts.map(opt => opt.value)
                                        }))}
                                        value={options.instituteName.filter((opt: {
                                            value: string;
                                        }) => filters.instituteName.includes(opt.value))}
                                        isMulti
                                    />
                                </TableCell>
                                {Object.keys(metadata).map(year => <TableCell align='center'>{year}</TableCell>)}
                            </TableRow>
                        </TableHead>,
                        Body: (props) => <>
                            <MTableBody {...props}/>
                            <TableFooter>
                                <TableRow>
                                    <TableCell/>
                                    <TableCell>Итого распределено в институты/ректорат: </TableCell>
                                    <TableCell/>
                                    {Object.entries(metadata).map(([_, metaValue]) => {
                                        const value = metaValue.total
                                        return <TableCell align='center' style={{color: 'black'}}>
                                            {numberFormatter.format(value)}
                                        </TableCell>
                                    })}
                                </TableRow>
                                <TableRow>
                                    <TableCell/>
                                    <TableCell>Нераспределённый остаток</TableCell>
                                    <TableCell/>
                                    {Object.entries(metadata).map(([_, metaValue]) => {
                                        const value = +(metaValue.totalKvrs - metaValue.total).toFixed(2)
                                        return <TableCell align='center' style={{color: value === 0 ? 'black' : 'crimson'}}>
                                            {numberFormatter.format(value)}
                                        </TableCell>;
                                    })}
                                </TableRow>
                            </TableFooter>
                        </>,
                    }}
                    title={''}
                    editable={{
                        onRowAdd: (value) => handleAddRow(value),
                        onRowUpdate: (value) => handleAddRow(value),
                        onRowDelete: (value) => handleDelete(value)
                    }}
                />
            </Card.Body>
        </Card>
    )
}
